import { ApolloClient } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { PayeeObjectTypeSortEnum } from "#graphql/supergraphEnums";
import { GetPayeesDocument } from "#graphql/supergraphHooks";
import { type GetPayeesQuery, type GetPayeesQueryVariables } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { toLoadPayeeSuccessParams } from "./utils";
import {
  applyPayeeFilter,
  type ExtendedPayeeFilterInput,
  type PayeeFilterModel,
} from "./utils/payeeFilterUtils";

/**
 * This class is used by AG Grid to fetch payees (paginated) via GQL
 * and pass them into the table.
 *
 * The payees are passed into toLoadSuccessParams which transforms
 * the data into row objects that are passed into AGGrid. toLoadSuccessParams also informs
 * AG Grid of the total number of rows so it knows when to stop paginating.
 *
 * The after property is used to store the cursor used to paginate through the payees.
 */

export class PayeesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private apiClient: ApolloClient<object>;
  private after: string;
  private rowCountPerPage: number;
  private refInputFilters: ExtendedPayeeFilterInput;

  constructor(client: ApolloClient<object>, rowCountPerPage: number) {
    this.apiClient = client;
    this.after = "";
    this.rowCountPerPage = rowCountPerPage;
    this.refInputFilters = {};
  }

  public getRows = (params: AgGrid.IServerSideGetRowsParams): void => {
    const { api, request } = params;
    const { filterModel } = request;

    api.showLoadingOverlay();

    const inputFilters: ExtendedPayeeFilterInput = {};
    applyPayeeFilter(inputFilters, filterModel as PayeeFilterModel);

    if (!isEqual(this.refInputFilters, inputFilters)) {
      this.after = "";
      this.refInputFilters = inputFilters;
    }

    const variables: GetPayeesQueryVariables = {
      first: this.rowCountPerPage,
      after: this.after,
      sort: [PayeeObjectTypeSortEnum.ModifiedAtDesc, PayeeObjectTypeSortEnum.StatusAsc],
      filters: inputFilters,
    };

    this.apiClient
      .query<GetPayeesQuery, GetPayeesQueryVariables>({
        query: GetPayeesDocument,
        context: clientContext(),
        variables,
      })
      .then((response) => {
        const successParams = toLoadPayeeSuccessParams(response.data);
        this.after = response.data.payees?.pageInfo?.endCursor || "";
        if (successParams.resp.rowCount === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }

        params.success(successParams.resp);
      })
      .catch((e: Error) => {
        addError(formatError(e));
        toast.error(`Fetching payees failed: ${e instanceof Error ? e.message : String(e)}`);
        api.hideOverlay();
        params.fail();
      });
  };
}
